/**
 * GovResourcesInline — compact 4-card grid linking to Canadian
 * government / regulator sources. Reused across every province landing
 * page + the homepage. Boosts E-E-A-T signals by citing authoritative
 * sources next to Central Rentals' own guides.
 *
 * `variant`:
 *   • "full"    (default) — same rich section used on the homepage
 *   • "compact"           — 2-column condensed grid for province sub-pages
 */
import { Home, Building2, Briefcase, Users, ShieldCheck, HandCoins, Scale } from "lucide-react";

export default function GovResourcesInline({ variant = "full", title, subtitle }) {
  const compact = variant === "compact";
  return (
    <section
      id="government-resources"
      data-testid="gov-resources-inline"
      className={`${compact ? "py-10" : "py-16 md:py-24"} bg-slate-50 border-y border-slate-200`}
    >
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 border border-emerald-200 text-[11px] font-extrabold tracking-[0.18em] uppercase text-emerald-800">
            <ShieldCheck className="w-3.5 h-3.5"/> Canadian Government Resources
          </div>
          <h2 className={`font-display mt-4 ${compact ? "text-2xl sm:text-3xl" : "text-3xl sm:text-4xl md:text-5xl"} font-extrabold tracking-tight text-slate-900 leading-tight`}>
            {title || "Housing, landlord, tenant, brokerage & tax info — straight from the source."}
          </h2>
          {!compact && (
            <p className="mt-4 text-base sm:text-lg text-slate-700 leading-relaxed">
              {subtitle || "Central Rentals Canada cross-references every rule against the official government registers below. Click any card to read the source — every link opens in a new tab so you can compare side-by-side."}
            </p>
          )}
        </div>

        <div className={`mt-8 grid grid-cols-1 md:grid-cols-2 gap-4`}>
          {[
            {
              testid: "housing", tone: "emerald",
              icon: Home, kicker: "Housing",
              title: "Canada Mortgage & Housing Corporation (CMHC)",
              blurb: "Federal housing agency. Rental market reports, affordable-housing programs, mortgage-loan insurance.",
              links: [
                { label: "Rental market data", href: "https://www.cmhc-schl.gc.ca/professionals/housing-markets-data-and-research", testid: "cmhc-rental" },
                { label: "Funding programs", href: "https://www.cmhc-schl.gc.ca/professionals/project-funding-and-mortgage-financing/funding-programs", testid: "cmhc-funding" },
                { label: "National Housing Strategy", href: "https://www.placetocallhome.ca/", testid: "nhs" },
                { label: "First-time home buyer", href: "https://www.cmhc-schl.gc.ca/consumers/home-buying/first-time-home-buyer-incentive", testid: "fthb" },
                { label: "CMHC homepage", href: "https://www.cmhc-schl.gc.ca/", testid: "cmhc-home" },
              ],
            },
            {
              testid: "landlord", tone: "sky",
              icon: Building2, kicker: "Landlords",
              title: "Provincial residential-tenancy tribunals",
              blurb: "Every province runs its own landlord-tenant board with hearings, forms, notices, and dispute rulings.",
              links: [
                { label: "Ontario LTB", href: "https://tribunalsontario.ca/ltb/", testid: "ltb" },
                { label: "Quebec TAL", href: "https://www.tal.gouv.qc.ca/en/", testid: "tal" },
                { label: "BC RTB", href: "https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies", testid: "rtb-bc" },
                { label: "Alberta RTDRS", href: "https://www.alberta.ca/rtdrs", testid: "rtdrs" },
                { label: "Manitoba RTB", href: "https://www.gov.mb.ca/cca/rtb/", testid: "rtb-mb" },
                { label: "Saskatchewan ORT", href: "https://www.saskatchewan.ca/residents/housing/renting-a-place-to-live", testid: "ort-sk" },
                { label: "Nova Scotia RTP", href: "https://beta.novascotia.ca/programs-and-services/residential-tenancies-program", testid: "rtp-ns" },
                { label: "NB Tribunal", href: "https://www2.snb.ca/content/snb/en/sites/residential-tenancies-tribunal.html", testid: "rtt-nb" },
              ],
            },
            {
              testid: "tenants", tone: "rose",
              icon: Users, kicker: "Tenants",
              title: "Tenant advocacy & rights organizations",
              blurb: "Free legal help, know-your-rights guides, and mediation clinics for renters across Canada.",
              links: [
                { label: "CERA (Centre for Equality Rights in Accommodation)", href: "https://www.equalityrights.org/", testid: "cera" },
                { label: "ACTO (Advocacy Centre for Tenants Ontario)", href: "https://www.acto.ca/", testid: "acto" },
                { label: "Steps to Justice — Housing (ON)", href: "https://stepstojustice.ca/legal-topic/housing-law/", testid: "stj" },
                { label: "TRAC Tenant Resource (BC)", href: "https://tenants.bc.ca/", testid: "trac" },
                { label: "Éducaloi — Housing (QC)", href: "https://educaloi.qc.ca/en/categories/housing/", testid: "educaloi" },
                { label: "Ontario Ombudsman", href: "https://www.ombudsman.on.ca/", testid: "ombuds-on" },
              ],
            },
            {
              testid: "brokerage", tone: "violet",
              icon: Briefcase, kicker: "Brokerages",
              title: "Provincial real-estate councils & regulators",
              blurb: "Licensing, trust-account rules, disciplinary rulings, and consumer protection for every registered brokerage.",
              links: [
                { label: "RECO (Ontario)", href: "https://www.reco.on.ca/", testid: "reco" },
                { label: "BCFSA (BC)", href: "https://www.bcfsa.ca/industry-resources/real-estate-professional-resources", testid: "bcfsa" },
                { label: "OACIQ (Quebec)", href: "https://www.oaciq.com/en", testid: "oaciq" },
                { label: "RECA (Alberta)", href: "https://www.reca.ca/", testid: "reca-ab" },
                { label: "SREC (Saskatchewan)", href: "https://www.srec.ca/", testid: "srec" },
                { label: "MREA (Manitoba)", href: "https://www.mrea.mb.ca/", testid: "mrea" },
                { label: "NSREC (Nova Scotia)", href: "https://www.nsrec.ns.ca/", testid: "nsrec" },
                { label: "CREA — Canadian Real Estate Assoc.", href: "https://www.crea.ca/", testid: "crea" },
              ],
            },
            {
              testid: "pm", tone: "amber",
              icon: Scale, kicker: "Property Managers",
              title: "CRA, StatCan & industry associations",
              blurb: "Tax reporting (T776, NR4, T5013), housing statistics, and the national trade bodies for licensed property managers.",
              links: [
                { label: "CRA T776 — Rental income", href: "https://www.canada.ca/en/revenue-agency/services/forms-publications/forms/t776.html", testid: "cra-t776" },
                { label: "CRA NR4 — Non-resident tax", href: "https://www.canada.ca/en/revenue-agency/services/forms-publications/forms/nr4.html", testid: "cra-nr4" },
                { label: "CRA Rental Income Guide (T4036)", href: "https://www.canada.ca/en/revenue-agency/services/forms-publications/publications/t4036.html", testid: "cra-t4036" },
                { label: "Statistics Canada — Housing", href: "https://www.statcan.gc.ca/en/subjects-start/housing", testid: "statcan" },
                { label: "CFAA — Federation of Apartment Assoc.", href: "https://www.cfaa-fcapi.org/", testid: "cfaa" },
                { label: "REIC — Real Estate Institute of Canada", href: "https://www.reic.ca/", testid: "reic" },
                { label: "IREM Canada", href: "https://www.irem.org/chapters/canada", testid: "irem" },
              ],
            },
            {
              testid: "finance", tone: "teal",
              icon: HandCoins, kicker: "Finance & Consumer",
              title: "Financial regulators & consumer protection",
              blurb: "Rules for handling deposits, credit checks, privacy, mortgage regulation, and anti-money-laundering compliance.",
              links: [
                { label: "FCAC — Financial Consumer Agency", href: "https://www.canada.ca/en/financial-consumer-agency.html", testid: "fcac" },
                { label: "OSFI — Bank regulator (B-20)", href: "https://www.osfi-bsif.gc.ca/", testid: "osfi" },
                { label: "OPC — Privacy Commissioner (PIPEDA)", href: "https://www.priv.gc.ca/en/", testid: "opc" },
                { label: "FINTRAC — AML/ATF", href: "https://www.fintrac-canafe.gc.ca/", testid: "fintrac" },
                { label: "Competition Bureau", href: "https://competition-bureau.canada.ca/", testid: "cb" },
                { label: "Bank of Canada — Rates", href: "https://www.bankofcanada.ca/rates/", testid: "boc" },
                { label: "Equifax Canada — Credit reports", href: "https://www.consumer.equifax.ca/", testid: "equifax" },
                { label: "TransUnion Canada", href: "https://www.transunion.ca/", testid: "transunion" },
              ],
            },
          ].map((card) => {
            const Icon = card.icon;
            const tone = card.tone;
            return (
              <div key={card.testid} className="p-5 rounded-2xl bg-white border border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all" data-testid={`gov-inline-card-${card.testid}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-11 h-11 rounded-xl bg-${tone}-100 text-${tone}-800 flex items-center justify-center flex-none`}>
                    <Icon className="w-6 h-6"/>
                  </div>
                  <div className="min-w-0">
                    <div className={`text-[11px] uppercase tracking-widest font-bold text-${tone}-700`}>{card.kicker}</div>
                    <h3 className="mt-1 font-display font-extrabold text-base sm:text-lg text-slate-900 leading-tight">{card.title}</h3>
                    <p className="mt-2 text-sm text-slate-700 leading-relaxed">{card.blurb}</p>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
                      {card.links.map((l) => (
                        <a key={l.testid} href={l.href} target="_blank" rel="noopener noreferrer" data-testid={`gov-inline-link-${l.testid}`}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-${tone}-50 text-${tone}-800 hover:bg-${tone}-100 border border-${tone}-200`}>
                          {l.label} →
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {!compact && (
          <div className="mt-6 text-center text-xs text-slate-500">
            External links open in a new tab.
          </div>
        )}
      </div>
    </section>
  );
}
